import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Image, TouchableOpacity } from 'react-native-web';
import Header from './Header';
import { getCampaigns, getCampaignCreators } from '../services/CampaignService';
import blueLandscapeIcon from '../icons/blue-landscape-icon.png';
import paymentIcon from "../icons/DupeIcons-22-Payment.png";
import calendarIcon from "../icons/DupeIcons-23-Calendar.png";
import downArrowIcon from "../icons/DupeIcons-03-Arrow.png";
import CreatorReviewForm from './CreatorReviewForm';

const ReviewsPage = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [expandedCampaignId, setExpandedCampaignId] = useState(null);
    const [campaignCreators, setCampaignCreators] = useState({});

    useEffect(() => {
        fetchCampaigns();
    }, []);

    useEffect(() => {
        if (expandedCampaignId) {
            fetchCampaignCreators(expandedCampaignId);
        }
    }, [expandedCampaignId]);

    function fetchCampaigns() {
        getCampaigns()
            .then(resp => {
                if (resp.status === 200 && resp.data) {
                    setCampaigns(resp.data);
                }
            });
    }

    function fetchCampaignCreators(campaignId) {
        getCampaignCreators(campaignId)
            .then(resp => {
                if (resp.status === 200 && resp.data) {
                    setCampaignCreators(prev => ({
                        ...prev,
                        [campaignId]: resp.data.active || []
                    }));
                }
            });
    }

    const formatDate = (campaign) => {
        if (!campaign.startDate || !campaign.endDate) {
            return 'Dates';
        }
        const monthFormatter = new Intl.DateTimeFormat('default', {month: 'long', day: 'numeric'});
        const startDate = Date.parse(campaign.startDate);
        const endDate = Date.parse(campaign.endDate);
        return monthFormatter.format(startDate) + ' - ' + monthFormatter.format(endDate);
    };

    const formatCompensation = (campaign) => {
        if (campaign.paymentCompensation && campaign.productCompensation) {
            return "Payment and Free Product";
        } else if (!campaign.paymentCompensation && campaign.productCompensation) {
            return "$0 and Free Product";
        } else if (campaign.paymentCompensation && !campaign.productCompensation) {
            return "Payment";
        } else {
            return "$0";
        }
    };

    const toggleCampaign = (campaignId) => {
        setExpandedCampaignId(expandedCampaignId === campaignId ? null : campaignId);
    };

    return (
        <div>
            <Header title="Reviews" />
            <View>
                <View style={styles.containerLabelDiv}>
                    <View style={styles.headerContainer}>
                        <Text style={styles.blueText}>Campaign Reviews</Text>
                    </View>
                    <Text style={styles.descriptionText}>
                        Review creators who have completed your campaigns. Rate their responsiveness and quality, and provide feedback to help improve future collaborations.
                    </Text>
                </View>
                <ScrollView>
                    <View>
                        {campaigns.map(campaign => (
                            <View key={campaign.id}>
                                <TouchableOpacity 
                                    style={styles.campaignPreviewContainer}
                                    onPress={() => toggleCampaign(campaign.id)}
                                >
                                    <Image 
                                        style={styles.campaignPreviewImage} 
                                        source={campaign.imageExists 
                                            ? {uri: 'https://d3p3fw3rutb1if.cloudfront.net/business/photos/' + campaign.id}
                                            : blueLandscapeIcon
                                        }
                                        defaultSource={blueLandscapeIcon}
                                        resizeMode="cover"
                                    />
                                    <View style={styles.contentContainer}>
                                        <View style={styles.leftColumn}>
                                            <View style={styles.campaignInfoContainer}>
                                                <View>
                                                    <Text style={styles.campaignNameText}>{campaign.name}</Text>
                                                </View>
                                                <View>
                                                    <View style={styles.infoRow}>
                                                        <Image style={styles.campaignPreviewIcon} source={paymentIcon}/>
                                                        <Text style={styles.subtleText}>{formatCompensation(campaign)}</Text>
                                                    </View>
                                                    <View style={styles.infoRow}>
                                                        <Image style={styles.campaignPreviewIcon} source={calendarIcon}/>
                                                        <Text style={styles.subtleText}>{formatDate(campaign)}</Text>
                                                    </View>
                                                </View>
                                            </View>
                                        </View>
                                        <View style={styles.rightColumn}>
                                            <Image 
                                                source={downArrowIcon} 
                                                style={[
                                                    styles.expandIcon,
                                                    expandedCampaignId === campaign.id && styles.expandIconRotated
                                                ]} 
                                            />
                                        </View>
                                    </View>
                                </TouchableOpacity>
                                {expandedCampaignId === campaign.id && (
                                    <View style={styles.creatorsContainer}>
                                        {campaignCreators[campaign.id]?.map(creator => (
                                            <CreatorReviewForm 
                                                key={creator.id} 
                                                creator={creator}
                                                campaignId={campaign.id}
                                                businessId={campaign.businessId}
                                            />
                                        ))}
                                        {(!campaignCreators[campaign.id] || campaignCreators[campaign.id].length === 0) && (
                                            <Text style={styles.noCreatorsText}>No creators have completed this campaign yet.</Text>
                                        )}
                                    </View>
                                )}
                            </View>
                        ))}
                    </View>
                </ScrollView>
            </View>
        </div>
    );
};

const styles = StyleSheet.create({
    containerLabelDiv: {
        marginLeft: '2.5%',
        marginRight: '2.5%',
        marginBottom: 20,
    },
    headerContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    blueText: {
        color: '#396de0',
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 10,
        paddingTop: 10,
    },
    descriptionText: {
        fontSize: 14,
        color: '#737373',
        fontFamily: "'lato', sans-serif",
        marginBottom: 0,
    },
    campaignPreviewContainer: {
        backgroundColor: 'white',
        borderWidth: 1,
        borderColor: '#b8cbf5',
        marginBottom: 15,
        borderRadius: 10,
        padding: 5,
        marginLeft: '2.5%',
        marginRight: '2.5%',
        display: 'flex',
        flexDirection: 'row',
        cursor: 'pointer',
    },
    campaignPreviewImage: {
        height: 100,
        width: 100,
        borderRadius: 5,
        margin: 10,
    },
    contentContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        flex: 1,
    },
    leftColumn: {
        justifyContent: "center",
    },
    rightColumn: {
        justifyContent: "center",
        paddingRight: 20,
    },
    campaignInfoContainer: {
        justifyContent: 'space-between',
        flex: 0.85,
    },
    campaignNameText: {
        color: '#737373',
        fontFamily: "'forma-djr-display', sans-serif",
        fontWeight: 900,
        fontSize: 24,
        margin: 2
    },
    subtleText: {
        color: '#737373',
        fontFamily: "'lato', sans-serif",
        fontSize: 12
    },
    campaignPreviewIcon: {
        height: 15,
        width: 15,
        marginRight: 5,
    },
    infoRow: {
        flexDirection: 'row',
        margin: 2,
    },
    expandIcon: {
        height: 20,
        width: 20,
    },
    expandIconRotated: {
        transform: [{ rotate: '180deg' }],
    },
    creatorsContainer: {
        marginLeft: '2.5%',
        marginRight: '2.5%',
        marginBottom: 15,
        padding: 15,
        backgroundColor: '#f8f8f8',
        borderRadius: 10,
    },
    noCreatorsText: {
        textAlign: 'center',
        color: '#737373',
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        fontStyle: 'italic',
    },
});

export default ReviewsPage; 